import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { RangeCalendar } from '@enfomentis/ui';
import { today, getLocalTimeZone, parseDate, isWeekend } from '@internationalized/date';
import type { DateValue } from '@internationalized/date';
import type { RangeValue } from '@react-types/shared';
import { Calendar as CalendarIcon, TrendingUp, AlertTriangle, Users, Globe, Target, Activity } from 'lucide-react';

export const RangeCalendarExamples: React.FC = () => {
  const [selectedRange, setSelectedRange] = useState<RangeValue<DateValue> | null>(null);
  const [tradingRange, setTradingRange] = useState<RangeValue<DateValue> | null>(null);
  const [focusedDate, setFocusedDate] = useState<DateValue>(today(getLocalTimeZone()));

  // Custom date availability logic for trading calendar
  const isTradingDay = (date: DateValue) => {
    // Mark weekends as unavailable for trading
    return isWeekend(date, 'en-US');
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        <div className="space-y-4">
          <h1 className="text-3xl font-bold">RangeCalendar Component</h1>
          <p className="text-lg text-muted-foreground">
            Date range selection with start and end dates, supporting non-contiguous ranges and custom availability rules.
          </p>
        </div>

        {/* Basic Usage */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Basic Usage</h3>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Default Range Calendar"
                />
                <p className="text-sm text-text-secondary text-center">Default range selection</p>
              </div>
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Range Calendar with default value"
                  defaultValue={{
                    start: parseDate("2024-12-20"),
                    end: parseDate("2024-12-25")
                  }}
                />
                <p className="text-sm text-text-secondary text-center">Pre-selected Christmas week range</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Color Variants */}
        <Card className="max-w-6xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Color Variants</h3>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="flex flex-col xl:flex-row gap-6 items-center justify-center">
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Primary Range Calendar"
                  color="primary"
                  defaultValue={{
                    start: today(getLocalTimeZone()),
                    end: today(getLocalTimeZone()).add({ days: 7 })
                  }}
                />
                <p className="text-sm text-text-secondary text-center">Primary</p>
              </div>
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Secondary Range Calendar"
                  color="secondary"
                  defaultValue={{
                    start: today(getLocalTimeZone()),
                    end: today(getLocalTimeZone()).add({ days: 7 })
                  }}
                />
                <p className="text-sm text-text-secondary text-center">Secondary</p>
              </div>
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Success Range Calendar"
                  color="success"
                  defaultValue={{
                    start: today(getLocalTimeZone()),
                    end: today(getLocalTimeZone()).add({ days: 7 })
                  }}
                />
                <p className="text-sm text-text-secondary text-center">Success</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Date Constraints */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Date Constraints & Validation</h3>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Range Calendar with min/max dates"
                  minValue={today(getLocalTimeZone())}
                  maxValue={today(getLocalTimeZone()).add({ months: 3 })}
                  defaultValue={{
                    start: today(getLocalTimeZone()).add({ days: 7 }),
                    end: today(getLocalTimeZone()).add({ days: 14 })
                  }}
                  color="warning"
                />
                <p className="text-sm text-text-secondary text-center">Limited to next 3 months from today</p>
              </div>
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Range Calendar with unavailable weekends"
                  defaultValue={{
                    start: today(getLocalTimeZone()).add({ days: 1 }),
                    end: today(getLocalTimeZone()).add({ days: 5 })
                  }}
                  isDateUnavailable={isTradingDay}
                  color="danger"
                />
                <p className="text-sm text-text-secondary text-center">Weekends marked as unavailable</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Multiple Months */}
        <Card className="max-w-7xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Multiple Month Views</h3>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="space-y-6">
              <div className="space-y-2">
                <RangeCalendar 
                  aria-label="Two month range calendar"
                  visibleMonths={2}
                  defaultValue={{
                    start: today(getLocalTimeZone()),
                    end: today(getLocalTimeZone()).add({ months: 1, days: 7 })
                  }}
                  color="primary"
                />
                <p className="text-sm text-text-secondary text-center">Two months for cross-month range selection</p>
              </div>
              
              <div className="space-y-2">
                <RangeCalendar 
                  aria-label="Three month range calendar"
                  visibleMonths={3}
                  defaultValue={{
                    start: today(getLocalTimeZone()),
                    end: today(getLocalTimeZone()).add({ months: 2, days: 15 })
                  }}
                  color="secondary"
                />
                <p className="text-sm text-text-secondary text-center">Three months for quarterly planning</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Enhanced Features */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Enhanced Features</h3>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Range Calendar with month/year pickers"
                  showMonthAndYearPickers
                  defaultValue={{
                    start: today(getLocalTimeZone()),
                    end: today(getLocalTimeZone()).add({ days: 14 })
                  }}
                  color="primary"
                />
                <p className="text-sm text-text-secondary text-center">With month and year picker dropdowns</p>
              </div>
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Read-only range calendar"
                  isReadOnly
                  defaultValue={{
                    start: parseDate("2024-07-04"),
                    end: parseDate("2024-07-10")
                  }}
                  color="foreground"
                />
                <p className="text-sm text-text-secondary text-center">Read-only range calendar (display only)</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Non-Contiguous Ranges */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Advanced Range Selection</h3>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Non-contiguous range selection"
                  allowsNonContiguousRanges
                  color="warning"
                />
                <p className="text-sm text-text-secondary text-center">Allows non-contiguous date ranges</p>
              </div>
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Standard contiguous range"
                  color="success"
                  defaultValue={{
                    start: today(getLocalTimeZone()),
                    end: today(getLocalTimeZone()).add({ days: 10 })
                  }}
                />
                <p className="text-sm text-text-secondary text-center">Standard contiguous range only</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Controlled State */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Controlled State</h3>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Controlled range selection"
                  value={selectedRange}
                  onChange={setSelectedRange}
                  color="primary"
                />
                <div className="text-sm text-text-secondary text-center">
                  <p className="font-medium">Selected Range:</p>
                  <p>Start: {selectedRange?.start ? selectedRange.start.toString() : 'None'}</p>
                  <p>End: {selectedRange?.end ? selectedRange.end.toString() : 'None'}</p>
                </div>
              </div>
              <div className="space-y-2 flex flex-col items-center">
                <RangeCalendar 
                  aria-label="Controlled focus"
                  focusedValue={focusedDate}
                  onFocusChange={setFocusedDate}
                  color="secondary"
                />
                <p className="text-sm text-text-secondary text-center">
                  Focused: {focusedDate.toString()}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Trading Application Example */}
        <Card className="max-w-6xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Trading Application Example</h3>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <RangeCalendar 
                    aria-label="Select trading date range"
                    value={tradingRange}
                    onChange={setTradingRange}
                    minValue={today(getLocalTimeZone())}
                    maxValue={today(getLocalTimeZone()).add({ months: 6 })}
                    isDateUnavailable={isTradingDay}
                    color="success"
                    showMonthAndYearPickers
                  />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Trading Period Selection</p>
                    <div className="text-sm text-text-secondary">
                      <p>Start: {tradingRange?.start ? tradingRange.start.toString() : 'Select start date'}</p>
                      <p>End: {tradingRange?.end ? tradingRange.end.toString() : 'Select end date'}</p>
                    </div>
                    <p className="text-xs text-text-tertiary">
                      • Weekends are unavailable for trading
                      • Future dates only (up to 6 months)
                      • Quick month/year navigation enabled
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 bg-surface rounded-lg border border-border">
                    <h4 className="font-semibold mb-2">Range Calendar Features</h4>
                    <ul className="text-sm text-text-secondary space-y-1">
                      <li>• Start and end date selection</li>
                      <li>• Business days only (weekends disabled)</li>
                      <li>• Future date constraints</li>
                      <li>• Non-contiguous range support</li>
                      <li>• International date support</li>
                      <li>• Keyboard navigation</li>
                      <li>• Multiple month views</li>
                      <li>• Rapid date navigation</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <h4 className="font-semibold mb-2 text-primary">Trading Use Cases</h4>
                    <ul className="text-sm text-text-secondary space-y-1">
                      <li>• Backtesting period selection</li>
                      <li>• Strategy deployment windows</li>
                      <li>• Performance analysis periods</li>
                      <li>• Risk assessment timeframes</li>
                      <li>• Earnings season tracking</li>
                      <li>• Holiday trading exclusions</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </PlaygroundPageLayout>
  );
};